import { ACCOUNT_TRANSACTION_CLASSIFICATIONS, REFUND_ORIGINS, REFUND_STATUSES } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { generatedFileSchema } from './generated-file.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

const reasonSchema = z.string().trim().min(1).max(500);

const positiveMinorSchema = z
  .string()
  .regex(/^\d{1,18}$/, 'must be an integer in minor units')
  .refine((value) => /[1-9]/.test(value), 'must be above zero');

/** A line a refund names — where the money came from, or the outgoing line that paid it. */
export const refundLineSchema = z.object({
  id: z.uuid(),
  organizationAccountId: z.uuid(),
  accountLabel: z.string(),
  bookedOn: isoDaySchema,
  amount: moneySchema,
  description: z.string().nullable(),
  counterpartyName: z.string().nullable(),
});

export type RefundLine = z.infer<typeof refundLineSchema>;

export const refundSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  origin: z.enum(REFUND_ORIGINS),
  status: z.enum(REFUND_STATUSES),
  amount: moneySchema,
  reason: z.string(),
  donation: z.object({ id: z.uuid(), referenceCode: z.string() }).nullable(),
  sourceLine: refundLineSchema.nullable(),
  paidLine: refundLineSchema.nullable(),
  requestedByUserId: z.uuid().nullable(),
  requestedByName: z.string().nullable(),
  requestedAt: z.string(),
  approvedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  cancelledByName: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancellationReason: z.string().nullable(),
});

export type Refund = z.infer<typeof refundSchema>;

export const refundListInputSchema = paginationInputSchema.extend({
  organizationId: z.uuid().optional(),
  status: z.enum(REFUND_STATUSES).optional(),
  donationId: z.uuid().optional(),
});

export type RefundListInput = z.infer<typeof refundListInputSchema>;

/** The refund list's filter, without its page: an export is every matching row. */
export const refundExportInputSchema = refundListInputSchema.omit({ page: true, pageSize: true });

export type RefundExportInput = z.infer<typeof refundExportInputSchema>;

/**
 * A line as the request dialog reads it: what is left of it, the donations it
 * has already paid — one of which the overpayment usually belongs to — and
 * whether a refund can be asked from it at all. The write judges it again.
 */
export const refundableLineSchema = z.object({
  line: refundLineSchema.extend({
    organizationId: z.uuid(),
    classification: z.enum(ACCOUNT_TRANSACTION_CLASSIFICATIONS),
    remaining: moneySchema,
  }),
  requestable: z.boolean(),
  donations: z.array(
    z.object({
      donationId: z.uuid(),
      referenceCode: z.string(),
      donorName: z.string().nullable(),
      credited: moneySchema,
    }),
  ),
});

export type RefundableLine = z.infer<typeof refundableLineSchema>;

export const requestRefundInputSchema = z.object({
  accountTransactionId: z.uuid(),
  amountMinor: positiveMinorSchema,
  reason: reasonSchema,
  donationId: z.uuid().optional(),
});

export type RequestRefundInput = z.infer<typeof requestRefundInputSchema>;

/**
 * Money owed back and its payment — docs/plans/05-finance-reconciliation.md
 * §5.8. Every read and write is scoped by the whole organization the money
 * belongs to; a refund is approved by someone other than the person who asked
 * for it, and paid against an outgoing line classified `refund_payout`.
 */
export const refundContract = {
  list: oc
    .route({ method: 'GET', path: '/refunds' })
    .errors(guarded)
    .input(refundListInputSchema)
    .output(paginated(refundSchema)),

  /** The whole refund filter as a file the worker writes — docs/decisions/0043-the-worker.md §7. */
  export: oc
    .route({ method: 'POST', path: '/refunds/export' })
    .errors(guarded)
    .input(refundExportInputSchema)
    .output(generatedFileSchema),

  refundableLine: oc
    .route({ method: 'GET', path: '/refunds/lines/{accountTransactionId}' })
    .errors(guarded)
    .input(z.object({ accountTransactionId: z.uuid() }))
    .output(refundableLineSchema),

  /** Allocates the amount from the line's remainder, so nobody matches it to a donation meanwhile. */
  request: oc
    .route({ method: 'POST', path: '/refunds' })
    .errors(guarded)
    .input(requestRefundInputSchema)
    .output(z.object({ refund: refundSchema })),

  approve: oc
    .route({ method: 'POST', path: '/refunds/{id}/approve' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ refund: refundSchema })),

  /** The organization's `refund_payout` debits of exactly the refund's amount that nothing holds yet. */
  payoutLines: oc
    .route({ method: 'GET', path: '/refunds/{id}/payout-lines' })
    .errors(guarded)
    .input(paginationInputSchema.extend({ id: z.uuid() }))
    .output(paginated(refundLineSchema)),

  pay: oc
    .route({ method: 'POST', path: '/refunds/{id}/pay' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), paidTransactionId: z.uuid() }))
    .output(z.object({ refund: refundSchema })),

  /** Withdraws a refund asked from a line's remainder, which returns to the line. */
  cancel: oc
    .route({ method: 'POST', path: '/refunds/{id}/cancel' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: reasonSchema }))
    .output(z.object({ refund: refundSchema })),
};
